import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Bell, CalendarPlus, ExternalLink, FileSpreadsheet, FileText, Lock, Paperclip, Printer, Send, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { userHasFeature } from "@/lib/plans";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import api, { downloadFile } from "@/lib/api";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DAY_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const dayName = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? "—" : DAY_AR[dt.getDay()];
};
const periodDays = (p) => {
  const days = String(p || "").split(" إلى ").map(dayName).filter((x) => x !== "—");
  return days.join(" إلى ");
};
import { letterHtml, lettersHtml } from "@/lib/inquiryForm";

const blobToDataUrl = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(blob);
});
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FLEX_SUBJECTS = ["عدم الالتزام بالمناوبة", "عدم الإلتزام بالإشراف",
  "عدم الإلتزام بحصة الانتظار", "عدم الإلتزام بوقت الحصة", "عدم التواجد بالمدرسة"];
const ABSENCE_SUBJECT = "عدم التواجد بالمدرسة";
const FLEX_SPECIAL = [
  { id: "__lateness", label: "مساءلة تأخر — تُعبأ بإجمالي دقائق التأخر الحالية" },
  { id: "__early", label: "مساءلة خروج مبكر — تُعبأ بإجمالي دقائق الانصراف الحالية" },
];
const ABSENCE_STATUSES = ["عذر طبي", "عذر اضطراري", "عذر مهمة عمل", "بدون عذر"];
const OTHER_STATUSES = ["عذر مقبول", "بدون عذر"];
const INTERIM_STATUSES = ["تم الإرسال", "قيد الرد", "تم الرد"];
const TYPE_LABELS = { absence: "غياب", lateness: "تأخر", early_leave: "انصراف مبكر", no_checkout: "عدم تسجيل بصمة الانصراف", flexible: "مرنة", mixed: "مجمعة (أنواع متعددة)" };
// المرنة تُفرَّز بموضوعها المحدد لا بكلمة «مرنة» (بلا موضوع لا تُدرج كخيار)
const typeKey = (q) => (q.inquiry_type === "flexible" ? q.subject : q.inquiry_type);
const typeLabel = (k) => TYPE_LABELS[k] || k;
const baseStatuses = (q) => (q.inquiry_type === "absence" ? ABSENCE_STATUSES : OTHER_STATUSES);

const readSavedDateFilters = () => {
  try { return JSON.parse(localStorage.getItem("accountability_date_filters") || "{}"); } catch { return {}; }
};

export default function AccountabilityTab() {
  const { user } = useAuth();
  const canSendPortal = userHasFeature(user, "portal");
  const [inquiries, setInquiries] = useState([]);
  const [branding, setBranding] = useState({});
  const [from, setFrom] = useState(() => readSavedDateFilters().from || dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => readSavedDateFilters().to || dayjs().format("YYYY-MM-DD"));
  const [letter, setLetter] = useState(null);
  const [replyView, setReplyView] = useState(null);
  const [flexOpen, setFlexOpen] = useState(false);
  const [flexEmployees, setFlexEmployees] = useState([]);
  const [flexEmployee, setFlexEmployee] = useState("");
  const [flexDate, setFlexDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [flexFrom, setFlexFrom] = useState("");
  const [flexTo, setFlexTo] = useState("");
  const [flexReason, setFlexReason] = useState("");
  const [flexSubject, setFlexSubject] = useState("");
  const [flexExitTime, setFlexExitTime] = useState("");
  const [flexReturnTime, setFlexReturnTime] = useState("");
  const [notes, setNotes] = useState([]);
  const [empFilter, setEmpFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (q) => {
    const next = new Set(selected);
    if (next.has(q.id)) {
      next.delete(q.id);
    } else {
      const firstId = [...next][0];
      const firstName = inquiries.find((x) => x.id === firstId)?.employee_name;
      if (next.size && firstName !== q.employee_name) {
        toast.error("يمكن تجميع مساءلات نفس الموظف فقط");
        return;
      }
      next.add(q.id);
    }
    setSelected(next);
  };

  const selectedInquiries = inquiries.filter((q) => selected.has(q.id));
  const mergeBlockReason = (() => {
    if (selectedInquiries.length < 2) return null;
    if (selectedInquiries.some((q) => q.inquiry_type === "flexible"))
      return "المساءلات المرنة لا تُجمع";
    if (selectedInquiries.some((q) => !q.auto))
      return "يمكن تجميع المساءلات التلقائية فقط";
    if (new Set(selectedInquiries.map((q) => q.inquiry_type)).size > 1)
      return "يمكن تجميع مساءلات من نفس النوع فقط";
    return null;
  })();

  const mergeSelected = async () => {
    try {
      await api.post("/inquiries/merge", { inquiry_ids: [...selected] });
      toast.success("تم تجميع المساءلات في مساءلة واحدة");
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر تجميع المساءلات");
    }
  };

  const unmerge = async (q) => {
    if (!window.confirm("فك تجميع هذه المساءلة وإعادة المساءلات الأصلية منفصلة كما كانت؟")) return;
    try {
      await api.post(`/inquiries/${q.id}/unmerge`);
      toast.success("تم فك التجميع وإعادة المساءلات منفصلة");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر فك التجميع");
    }
  };

  const load = () => {
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
    api.get("/portal/notifications").then((r) => setNotes(r.data)).catch(() => {});
  };
  useEffect(load, [from, to]);

  useEffect(() => {
    localStorage.setItem("accountability_date_filters", JSON.stringify({ from, to }));
  }, [from, to]);

  const changeStatus = async (id, status) => {
    try {
      const r = await api.patch(`/inquiries/${id}`, { status });
      if (r.data?.deduction_created) toast.success("أُنشئ قرار حسم غياب تلقائياً — تبويب قرارات الحسم");
      load();
    } catch { toast.error("فشل التحديث"); }
  };

  const sendToPortal = async (q) => {
    try {
      await api.post(`/portal/inquiries/${q.id}/send`);
      toast.success(`أُرسلت المساءلة إلى ${q.employee_name} عبر بوابة الموظفين`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الإرسال"); }
  };

  const openReplyView = async (q) => {
    let sigUrl = null;
    if (q.reply_signature_path) {
      try {
        const r = await api.get(`/portal/inquiries/${q.id}/signature`, { responseType: "blob" });
        sigUrl = URL.createObjectURL(r.data);
      } catch { /* بدون توقيع */ }
    }
    let attach = null;
    if (q.reply_attachment_path) {
      try {
        const r = await api.get(`/portal/inquiries/${q.id}/attachment`, { responseType: "blob" });
        attach = { url: URL.createObjectURL(r.data), type: r.data.type || q.reply_attachment_type || "" };
      } catch { /* بدون مرفق */ }
    }
    setReplyView({ q, sigUrl, attach });
  };

  const bulkDeleteInquiries = async (mode) => {
    const targets = mode === "selected" ? inquiries.filter((q) => selected.has(q.id)) : visibleInquiries;
    if (!targets.length) return;
    setDeleting(true);
    try {
      let deleted = 0;
      for (let i = 0; i < targets.length; i += 500) {
        const { data } = await api.post("/inquiries/bulk-delete",
          { ids: targets.slice(i, i + 500).map((q) => q.id) });
        deleted += data.deleted;
      }
      toast.success(`تم حذف ${deleted} مساءلة نهائياً`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحذف"); }
    finally { setDeleting(false); }
  };

  const deleteAllInquiries = async () => {
    if (!window.confirm("تنبيه: سيتم حذف جميع المسائلات نهائياً. هل أنت متأكد؟")) return;
    if (!window.confirm("تأكيد أخير: لا يمكن التراجع عن هذا الإجراء. متابعة حذف كل المسائلات؟")) return;
    try {
      const { data } = await api.delete("/inquiries");
      toast.success(`تم حذف ${data.deleted} مساءلة`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch { toast.error("فشل الحذف"); }
  };

  const deleteInquiry = async (q) => {
    if (!window.confirm(`حذف مساءلة "${q.title}" للموظف ${q.employee_name}؟`)) return;
    await api.delete(`/inquiries/${q.id}`).then(() => { toast.success("تم حذف المساءلة"); load(); })
      .catch(() => toast.error("فشل الحذف"));
  };

  const pdfToImages = async (buf) => {
    const pdf = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
    const imgs = [];
    for (let i = 1; i <= pdf.numPages; i += 1) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: Math.min(3, 1500 / page.getViewport({ scale: 1 }).width) });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      imgs.push(canvas.toDataURL("image/jpeg", 0.92));
    }
    return imgs;
  };

  const withReplyAssets = async (q) => {
    if (!q) return q;
    let out = q;
    if (q.reply_signature_path) {
      try {
        const r = await api.get(`/portal/inquiries/${q.id}/signature`, { responseType: "blob" });
        out = { ...out, reply_signature_data: await blobToDataUrl(r.data) };
      } catch { /* بدون توقيع */ }
    }
    if (q.reply_attachment_path) {
      try {
        const r = await api.get(`/portal/inquiries/${q.id}/attachment`, { responseType: "blob" });
        const ctype = r.data.type || q.reply_attachment_type || "";
        if (ctype.startsWith("image/")) {
          out = { ...out, reply_attachment_images: [await blobToDataUrl(r.data)] };
        } else if (ctype === "application/pdf") {
          out = { ...out, reply_attachment_images: await pdfToImages(await r.data.arrayBuffer()) };
        }
      } catch { /* بدون مرفق */ }
    }
    return out;
  };

  const waitForPrintImages = (doc) => {
    const imgs = [...doc.querySelectorAll("img")];
    if (!imgs.length) return Promise.resolve();
    return Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((res) => {
        img.onload = () => res();
        img.onerror = () => res();
        if (img.decode) img.decode().then(res).catch(() => {});
        setTimeout(res, 4000);
      });
    }));
  };

  const printLetter = (inq) => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("المتصفح منع النافذة المنبثقة — اسمح بالنوافذ المنبثقة ثم أعد المحاولة");
      return;
    }
    withReplyAssets(inq).then((full) => {
      w.document.write(letterHtml(full, branding));
      w.document.close();
      // الصور base64 لكن المتصفح يفك ترميزها بشكل غير متزامن — ننتظر اكتمالها
      // جميعاً قبل الطباعة حتى لا تختفي من ملف PDF المطبوع
      waitForPrintImages(w.document).then(() => {
        w.focus();
        w.print();
      });
    });
  };

  const inCurrentPeriod = (q) => {
    const dates = String(q.period || "").split(" إلى ").map((s) => s.trim())
      .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
    if (!dates.length) return true;
    return dates[0] <= to && dates[dates.length - 1] >= from;
  };
  const periodInquiries = inquiries.filter(inCurrentPeriod);
  const empNames = [...new Set(inquiries.map((q) => q.employee_name).filter(Boolean))];
  const visibleInquiries = periodInquiries
    .filter((q) => empFilter === "all" || q.employee_name === empFilter)
    .filter((q) => typeFilter === "all" || typeKey(q) === typeFilter);
  const typeOptions = [...new Set(inquiries.map(typeKey).filter(Boolean))];

  const printAllInquiries = () => {
    if (!visibleInquiries.length) {
      toast.error("لا توجد مساءلات مطابقة للتصفية المحددة");
      return;
    }
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("المتصفح منع النافذة المنبثقة — اسمح بالنوافذ المنبثقة ثم أعد المحاولة");
      return;
    }
    Promise.all(visibleInquiries.map(withReplyAssets)).then((full) => {
      w.document.write(lettersHtml(full, branding));
      w.document.close();
      waitForPrintImages(w.document).then(() => {
        w.focus();
        w.print();
      });
    });
  };

  const exportReport = async (kind) => {
    try {
      const emp = empFilter !== "all" ? `&employees=${encodeURIComponent(empFilter)}` : "";
      const typ = typeFilter !== "all" ? `&qtype=${encodeURIComponent(typeFilter)}` : "";
      await downloadFile(`/export/${kind}?report=accountability&frm=${from}&to=${to}${emp}${typ}`,
        `تقرير-المسائلات-${from}.${kind === "excel" ? "xlsx" : "pdf"}`);
      toast.success("تم تصدير التقرير");
    } catch { toast.error("فشل التصدير"); }
  };

  const openFlexible = () => {
    setFlexEmployee("");
    setFlexReason("");
    setFlexSubject("");
    setFlexDate(dayjs().format("YYYY-MM-DD"));
    setFlexFrom("");
    setFlexTo("");
    setFlexExitTime("");
    setFlexReturnTime("");
    setFlexOpen(true);
    api.get("/attendance/employees").then((r) => setFlexEmployees(r.data)).catch(() => setFlexEmployees([]));
  };

  const submitFlexible = async () => {
    let emp = flexEmployees.find((e) => e.employee_name === flexEmployee);
    const special = FLEX_SPECIAL.find((t) => t.id === flexSubject);
    const useRange = special && flexFrom && flexTo;
    if (useRange) {
      try {
        const { data } = await api.get("/attendance/employees",
          { params: { frm: flexFrom, to: flexTo } });
        emp = data.find((e) => e.employee_name === flexEmployee) || emp;
      } catch { /* تُستخدم الإجماليات العامة عند التعذر */ }
    }
    const period = useRange ? `${flexFrom} إلى ${flexTo}` : flexDate;
    const payload = special
      ? {
          employee_name: flexEmployee, employee_id: emp?.employee_id || null,
          inquiry_type: special.id === "__lateness" ? "lateness" : "early_leave",
          late_days: emp?.late_days || 0, late_minutes: emp?.total_late_minutes || 0,
          absence_days: 0,
          early_days: emp?.early_days || 0, early_minutes: emp?.total_early_minutes || 0,
          subject: null, custom_reason: flexReason.trim() || null, period,
        }
      : {
          employee_name: flexEmployee, employee_id: emp?.employee_id || null,
          inquiry_type: "flexible", late_days: 0, late_minutes: 0,
          absence_days: 0, early_days: 0, early_minutes: 0,
          subject: flexSubject,
          custom_reason: flexReason.trim(), period: flexDate,
          ...(flexSubject === ABSENCE_SUBJECT
            ? { exit_time: flexExitTime || null, return_time: flexReturnTime || null }
            : {}),
        };
    try {
      const { data } = await api.post("/inquiries", payload);
      toast.success("تم إنشاء المساءلة");
      setFlexOpen(false);
      setLetter(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر إنشاء المساءلة");
    }
  };

  return (
    <div className="space-y-4" data-testid="accountability-tab">
      {notes.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm p-4 flex items-center" data-testid="accountability-notes-strip">
          <p className="font-bold text-sm text-emerald-950">آخر الإشعارات</p>
          <div className="mr-auto flex items-center gap-1.5 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
            data-testid="accountability-notes-badge">
            <Bell className="w-3.5 h-3.5" /> {notes.length} إشعار
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="accountability-notifications">
          <div className="space-y-1.5">
            {notes.slice(0, 5).map((n, i) => (
              <p key={i} className="text-xs text-slate-600 leading-5" data-testid={`accountability-note-${i}`}>
                • {n.text} <span className="text-slate-400" dir="ltr">({(n.created_at || "").slice(0, 10)})</span>
              </p>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3 className="font-bold">المسائلات المسجلة</h3>
          <p className="text-[11px] text-slate-400">تُنشأ تلقائياً عند تجاوز العتبات المضبوطة في الإعدادات وعبر فحص يومي</p>
          <div className="flex items-center gap-2">
            <DateInput type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="accountability-date-from" className="w-36 h-9 text-xs" dir="ltr" />
            <span className="text-xs text-slate-400">إلى</span>
            <DateInput type="date" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="accountability-date-to" className="w-36 h-9 text-xs" dir="ltr" />
          </div>
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger data-testid="accountability-employee-filter" className="w-40 h-9 text-xs">
              <SelectValue placeholder="كل الموظفين" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-emp-all">كل الموظفين</SelectItem>
              {empNames.map((n) => (
                <SelectItem key={n} value={n} data-testid={`filter-emp-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="accountability-type-filter" className="w-44 h-9 text-xs">
              <SelectValue placeholder="كل الأنواع" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-type-all">كل أنواع المسائلات</SelectItem>
              {typeOptions.map((t) => (
                <SelectItem key={t} value={t} data-testid={`filter-type-${t}`}>{typeLabel(t)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected.size >= 2 && (
            <span className="flex items-center gap-2">
              <Button size="sm" onClick={mergeSelected} disabled={!!mergeBlockReason}
                title={mergeBlockReason || undefined}
                data-testid="btn-merge-inquiries"
                className="h-9 text-xs bg-emerald-800 hover:bg-emerald-900 text-white disabled:opacity-50">
                تجميع المساءلات ({selected.size})
              </Button>
              {mergeBlockReason && (
                <span className="text-[11px] text-amber-700" data-testid="merge-block-reason">
                  {mergeBlockReason}
                </span>
              )}
            </span>
          )}
          {selected.size > 0 && (
            <button type="button" onClick={() => setSelected(new Set())} data-testid="btn-clear-selection"
              className="text-xs text-slate-400 hover:text-rose-600">إلغاء التحديد</button>
          )}
          <div className="mr-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)}
              disabled={visibleInquiries.length === 0}
              data-testid="btn-delete-inquiries"
              className="h-9 text-xs gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" /> حذف المسائلات{selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
            <Button size="sm" onClick={openFlexible}
              data-testid="btn-flexible-inquiry"
              className="h-9 text-xs gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
              <CalendarPlus className="w-4 h-4" /> مساءلة مرنة
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportReport("excel")}
              data-testid="accountability-export-excel" className="h-9 text-xs gap-1.5">
              <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportReport("pdf")}
              data-testid="accountability-export-pdf" className="h-9 text-xs gap-1.5">
              <FileText className="w-4 h-4 text-rose-700" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={printAllInquiries}
              disabled={!visibleInquiries.length}
              data-testid="btn-print-all-inquiries" className="h-9 text-xs gap-1.5">
              <Printer className="w-3.5 h-3.5" /> طباعة مساءلات الفترة ({visibleInquiries.length})
            </Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="inquiries-list">
          {inquiries.length === 0 && <p className="text-sm text-slate-400">لا توجد مسائلات مسجلة بعد</p>}
          {inquiries.length > 0 && visibleInquiries.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="inquiries-filtered-empty">لا توجد مساءلات ضمن الفترة أو للموظف المحدد</p>
          )}
          {visibleInquiries.map((q) => (
            <div key={q.id} className="flex flex-wrap items-center gap-3 border rounded-xl px-4 py-3">
              {!q.merged && (
                <input type="checkbox" checked={selected.has(q.id)} onChange={() => toggleSelect(q)}
                  data-testid={`select-inquiry-${q.id}`} title="تحديد للتجميع"
                  className="w-4 h-4 accent-emerald-800 shrink-0 cursor-pointer" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm">{q.employee_name} — <span className="text-emerald-800">{q.title}</span>
                  {q.merged && <span className="text-[10px] font-bold text-violet-800 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 mr-1.5" data-testid={`merged-badge-${q.id}`}>مجمعة</span>}
                  {q.subject && <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-1.5" data-testid={`inquiry-subject-${q.id}`}>{q.subject}</span>}
                  {q.source && <span className="text-[10px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 mr-1.5" data-testid={`inquiry-source-${q.id}`}>{q.source}</span>}
                </p>
                <p className="text-[11px] text-slate-400">{q.period}{periodDays(q.period) ? ` — ${periodDays(q.period)}` : ""}</p>
              </div>
              {!q.portal_sent && q.status !== "مغلقة" && canSendPortal && (
                <Button size="sm" onClick={() => sendToPortal(q)}
                  data-testid={`btn-send-portal-${q.id}`}
                  className="h-8 text-xs gap-1.5 bg-blue-700 hover:bg-blue-800 text-white">
                  <Send className="w-3.5 h-3.5" /> إرسال للموظف
                </Button>
              )}
              {!q.portal_sent && q.status !== "مغلقة" && !canSendPortal && (
                <button type="button" data-testid={`btn-send-portal-locked-${q.id}`}
                  title="متاح في الباقة البريميوم فأعلى — رقِّ اشتراكك"
                  onClick={() => toast.error("إرسال المساءلات للموظفين عبر البوابة متاح في الباقة البريميوم فأعلى — رقِّ اشتراكك من تبويب «اشتراكي»")}
                  className="h-8 px-3 rounded-md text-xs font-bold gap-1.5 inline-flex items-center bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed">
                  <Lock className="w-3.5 h-3.5" /> إرسال للموظف
                </button>
              )}
              {q.portal_sent && !q.reply_text && q.status !== "مغلقة" && (
                <span data-testid={`badge-awaiting-reply-${q.id}`}
                  className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1">
                  بانتظار رد الموظف
                </span>
              )}
              {q.reply_text && (
                <Button size="sm" variant="outline" onClick={() => openReplyView(q)}
                  data-testid={`btn-view-reply-${q.id}`}
                  className="h-8 text-xs gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                  عرض الرد
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={async () => setLetter(await withReplyAssets(q))}
                data-testid={`btn-view-inquiry-${q.id}`} className="h-8 text-xs gap-1.5">
                معاينة النموذج
              </Button>
              <Button size="sm" variant="outline" onClick={() => printLetter(q)}
                data-testid={`btn-print-inquiry-${q.id}`} className="h-8 text-xs gap-1.5">
                <Printer className="w-3.5 h-3.5" /> طباعة
              </Button>
              {q.merged && (
                <Button size="sm" variant="outline" onClick={() => unmerge(q)}
                  data-testid={`btn-unmerge-${q.id}`}
                  className="h-8 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-50">
                  فك التجميع
                </Button>
              )}
              <Select value={q.status} onValueChange={(s) => changeStatus(q.id, s)}>
                <SelectTrigger data-testid={`inquiry-status-${q.id}`} className="w-32 h-8 text-xs">
                  <SelectValue>{INTERIM_STATUSES.includes(q.status || "تم الإرسال") ? "الإجراء" : `الإجراء: ${q.status}`}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {baseStatuses(q).map((s) => (
                    <SelectItem key={s} value={s}
                      data-testid={`status-option-${q.id}-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={() => deleteInquiry(q)} data-testid={`btn-delete-inquiry-${q.id}`}
                className="text-rose-500 hover:text-rose-700 p-1.5">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={flexOpen} onOpenChange={setFlexOpen}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="flexible-inquiry-modal">
          <DialogHeader><DialogTitle>إنشاء مساءلة مرنة</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">الموظف</p>
              <Select value={flexEmployee} onValueChange={setFlexEmployee}>
                <SelectTrigger data-testid="flex-employee-select" className="h-10 text-sm">
                  <SelectValue placeholder="اختر الموظف" />
                </SelectTrigger>
                <SelectContent>
                  {flexEmployees.map((e) => (
                    <SelectItem key={e.employee_name} value={e.employee_name}
                      data-testid={`flex-emp-${e.employee_name}`}>{e.employee_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">التاريخ</p>
              <DateInput type="date" value={flexDate} onChange={(e) => setFlexDate(e.target.value)}
                data-testid="flex-date" className="h-10 text-sm" dir="ltr" />
            </div>
            {FLEX_SPECIAL.some((t) => t.id === flexSubject) && (
              <div className="space-y-1.5" data-testid="flex-range-section">
                <p className="text-xs font-semibold text-slate-500">احتساب الدقائق لفترة محددة (اختياري)</p>
                <div className="grid grid-cols-2 gap-2">
                  <DateInput type="date" value={flexFrom} onChange={(e) => setFlexFrom(e.target.value)}
                    data-testid="flex-from" className="h-10 text-sm" dir="ltr" />
                  <DateInput type="date" value={flexTo} onChange={(e) => setFlexTo(e.target.value)}
                    data-testid="flex-to" className="h-10 text-sm" dir="ltr" />
                </div>
                <p className="text-[11px] text-slate-400 leading-5">
                  عند تحديد الفترة تُحتسب دقائق {flexSubject === "__lateness" ? "التأخر" : "الانصراف المبكر"}
                  {" "}وتُنشأ المساءلة بها ضمن هذه الفترة فقط، وإلا تُستخدم الإجماليات الكاملة.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">اسم المساءلة</p>
              <Select value={flexSubject} onValueChange={setFlexSubject}>
                <SelectTrigger data-testid="flex-subject-select" className="h-10 text-sm">
                  <SelectValue placeholder="اختر اسم المساءلة" />
                </SelectTrigger>
                <SelectContent>
                  {FLEX_SPECIAL.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`flex-subject-${t.id.slice(2)}`}>{t.label}</SelectItem>
                  ))}
                  {FLEX_SUBJECTS.map((s, i) => (
                    <SelectItem key={s} value={s} data-testid={`flex-subject-opt-${i}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {flexSubject === ABSENCE_SUBJECT && (
              <div className="space-y-1.5" data-testid="flex-absence-times">
                <p className="text-xs font-semibold text-slate-500">وقتا الخروج والعودة</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-[11px] text-slate-400">وقت الخروج من المدرسة</p>
                    <Input type="time" value={flexExitTime} onChange={(e) => setFlexExitTime(e.target.value)}
                      data-testid="flex-exit-time" className="h-10 text-sm" dir="ltr" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] text-slate-400">وقت العودة (اختياري)</p>
                    <Input type="time" value={flexReturnTime} onChange={(e) => setFlexReturnTime(e.target.value)}
                      data-testid="flex-return-time" className="h-10 text-sm" dir="ltr" />
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 leading-5">
                  إن لم تحدد وقت العودة تُحتسب المدة حتى نهاية اليوم الدراسي تلقائياً.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">تفاصيل إضافية (اختياري)</p>
              <Textarea value={flexReason} onChange={(e) => setFlexReason(e.target.value)}
                data-testid="flex-reason" rows={3} maxLength={180}
                placeholder="تفاصيل إضافية تظهر في نص الخطاب..." />
              <p className="text-[11px] text-slate-400">{flexReason.length}/180 حرف</p>
            </div>
            <Button onClick={submitFlexible}
              disabled={!flexEmployee || !flexDate || !flexSubject || (flexSubject === ABSENCE_SUBJECT && !flexExitTime)}
              data-testid="btn-confirm-flexible"
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">
              إنشاء النموذج الرسمي
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyView} onOpenChange={() => setReplyView(null)}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="reply-view-dialog">
          <DialogHeader><DialogTitle>رد الموظف — {replyView?.q.employee_name}</DialogTitle></DialogHeader>
          {replyView && (
            <div className="space-y-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                <p className="text-sm text-slate-700 leading-7 whitespace-pre-wrap"
                  data-testid="reply-view-text">{replyView.q.reply_text}</p>
              </div>
              {replyView.sigUrl && (
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-slate-500">التوقيع الإلكتروني</p>
                  <img src={replyView.sigUrl} alt="توقيع الموظف" data-testid="reply-view-signature"
                    className="max-h-24 border rounded-lg bg-white p-1" />
                </div>
              )}
              {replyView.attach && (
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-slate-500">مستند العذر المرفق</p>
                  {replyView.attach.type.startsWith("image/") ? (
                    <img src={replyView.attach.url} alt="مستند العذر" data-testid="reply-view-attachment"
                      className="max-h-56 border rounded-lg bg-white p-1" />
                  ) : (
                    <Button variant="outline" size="sm" data-testid="reply-view-attachment"
                      onClick={() => window.open(replyView.attach.url, "_blank")} className="gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> عرض المستند (PDF)
                    </Button>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-400" dir="ltr" data-testid="reply-view-date">
                {(replyView.q.replied_at || "").slice(0, 16).replace("T", " ")}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!letter} onOpenChange={() => setLetter(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="inquiry-letter-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{letter?.title} — نموذج رسمي</span>
              <button onClick={() => setLetter(null)} data-testid="btn-close-letter-modal"
                className="text-slate-400 hover:text-slate-700 p-1">
                <X className="w-5 h-5" />
              </button>
            </DialogTitle>
          </DialogHeader>
          {letter && (
            <iframe title="معاينة النموذج" data-testid="inquiry-letter-preview"
              srcDoc={letterHtml(letter, branding)}
              className="w-full h-[62vh] border rounded-xl bg-white" />
          )}
          <Button onClick={() => printLetter(letter)} data-testid="btn-print-letter"
            className="bg-emerald-900 hover:bg-emerald-950 text-white gap-2">
            <Printer className="w-4 h-4" /> طباعة النموذج الرسمي
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" dir="rtl" data-testid="bulk-delete-inquiries-modal">
          <DialogHeader><DialogTitle>حذف المسائلات</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 leading-6">
              اختر نطاق الحذف: المسائلات المحددة، أو كل الظاهر على الشاشة وفق التصفية
              (من {from} إلى {to})، أو جميع المسائلات نهائياً. الحذف نهائي ولا يمكن التراجع عنه.
            </p>
            <div className="space-y-2">
              <Button onClick={() => bulkDeleteInquiries("selected")} disabled={deleting || selected.size === 0}
                data-testid="btn-confirm-delete-inq-selected"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف المسائلات المحددة ({selected.size})
              </Button>
              <Button onClick={() => bulkDeleteInquiries("visible")} disabled={deleting || visibleInquiries.length === 0}
                data-testid="btn-confirm-delete-inq-visible" variant="outline"
                className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف كل المسائلات الظاهرة ({visibleInquiries.length})
              </Button>
              <Button onClick={deleteAllInquiries} disabled={deleting}
                data-testid="btn-confirm-delete-inq-all"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف جميع المسائلات نهائياً
              </Button>
            </div>
            {deleting && <p className="text-xs text-slate-400 text-center">جارٍ الحذف...</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
